import { useState, useEffect } from 'react'
import { Plug } from 'lucide-react'
import { TextField } from '../components/ui'
import { Button } from '../components/ui/Button'
import { Select } from '../components/ui/Select'
import { useI18n } from '../i18n'
import { useAuth } from '../components/AuthProvider'
import { API_ENDPOINTS } from '../config/api'
import { featureFlags } from '../config/featureFlags'
import EnterpriseOperationalPage from '../components/enterprise-operational/EnterpriseOperationalPage'
import EnterpriseOperationalSection, {
  EnterpriseOperationalCard,
} from '../components/enterprise-operational/EnterpriseOperationalSection'
import entStyles from '../styles/enterprise/EnterpriseOperationalUx.module.scss'
import styles from '@/styles/pages/Integrations.module.scss'
import AiIntegrationsPhase3aPanel from '../components/integrations/AiIntegrationsPhase3aPanel'

const AI_PROVIDER_OPTIONS = [
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'claude', label: 'Anthropic (Claude)' },
  { value: 'azure', label: 'Azure OpenAI' },
  { value: 'mistral', label: 'Mistral' },
  { value: 'groq', label: 'Groq' },
  { value: 'vertex', label: 'Google Vertex AI' },
  { value: 'ollama', label: 'Ollama (local)' },
  { value: 'custom', label: 'Custom (OpenAI-compatible)' },
] as const

const AI_MODEL_OPTIONS = [
  { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (Default)' },
  { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
  { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
  { value: 'gpt-4o-mini', label: 'GPT-4o-mini' },
  { value: 'gpt-4', label: 'GPT-4' },
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5-turbo' },
] as const

export default function Integrations() {
  const { t } = useI18n()
  const { token, user } = useAuth()
  const enterpriseUx = featureFlags.ENTERPRISE_OPERATIONAL_UX_V1
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin'
  const loadLegacyAiStatus = featureFlags.AI_AUDIT_ASSISTANT_V1
  const loadFoundationRuntime = loadLegacyAiStatus && isAdmin
  const [aiProvider, setAiProvider] = useState<string>('gemini')
  const [aiConfig, setAiConfig] = useState<Record<string, string>>({
    apiKey: '',
    model: 'gemini-2.0-flash',
    endpoint: 'https://generativelanguage.googleapis.com',
    apiVersion: 'v1beta',
    projectNumber: '',
    headers: '',
    bodyTemplate: '',
  })
  const [secureInferenceMode, setSecureInferenceMode] = useState(false)
  const [localModelMode, setLocalModelMode] = useState(false)
  const [aiProviderCount, setAiProviderCount] = useState(0)
  const [aiModelCount, setAiModelCount] = useState(0)
  const [aiHealthStatus, setAiHealthStatus] = useState('UNKNOWN')
  const [runtimeStatus, setRuntimeStatus] = useState<any>(null)
  const [runtimeMetrics, setRuntimeMetrics] = useState<any>(null)
  const [runtimeRulepacks, setRuntimeRulepacks] = useState<any>(null)
  const [runtimeHealth, setRuntimeHealth] = useState<any>(null)

  useEffect(() => {
    let active = true
    setAiConfig((prev) => ({ ...prev, apiKey: '' }))
    const loadAiFoundation = async () => {
      if (!token) return
      const headers = { Authorization: `Bearer ${token}` }
      try {
        const providersRes = await fetch(API_ENDPOINTS.AI_PROVIDERS_V2, { headers })
        if (!active) return
        if (providersRes.ok) {
          const providersJson = await providersRes.json().catch(() => ({}))
          const providers = Array.isArray(providersJson?.data?.providers)
            ? providersJson.data.providers
            : []
          setAiProviderCount(providers.length)
          setAiModelCount(
            providers.filter((p: { model?: string; embeddingModel?: string }) => p.model || p.embeddingModel)
              .length,
          )
          setAiHealthStatus(String(providersJson?.data?.runtimeMode || 'UNCONFIGURED'))
          if (providers[0]) {
            setSecureInferenceMode(Boolean(providers[0]?.secureMode))
            setLocalModelMode(Boolean(providers[0]?.localMode))
          }
        }

        if (loadLegacyAiStatus) {
          const modelsRes = await fetch(API_ENDPOINTS.AI_MODELS, { headers })
          if (!active) return
          if (modelsRes.ok) {
            const modelsJson = await modelsRes.json().catch(() => ({}))
            const models = Array.isArray(modelsJson?.data) ? modelsJson.data : []
            setAiModelCount(models.length)
          }
          const healthRes = await fetch(API_ENDPOINTS.AI_HEALTH, { headers })
          if (!active) return
          if (healthRes.ok) {
            const healthJson = await healthRes.json().catch(() => ({}))
            setAiHealthStatus(String(healthJson?.data?.status || 'UNKNOWN'))
          }
        }

        if (loadFoundationRuntime) {
          const [runtimeStatusRes, runtimeMetricsRes, runtimeRulepacksRes, runtimeHealthRes] =
            await Promise.all([
              fetch(`${API_ENDPOINTS.AI_FOUNDATION_RUNTIME_STATUS}?apiVersion=v1`, { headers }),
              fetch(`${API_ENDPOINTS.AI_FOUNDATION_RUNTIME_METRICS}?apiVersion=v1`, { headers }),
              fetch(`${API_ENDPOINTS.AI_FOUNDATION_RUNTIME_RULEPACKS}?apiVersion=v1`, { headers }),
              fetch(`${API_ENDPOINTS.AI_FOUNDATION_RUNTIME_HEALTH}?apiVersion=v1`, { headers }),
            ])
          if (!active) return
          if (runtimeStatusRes.ok) {
            const json = await runtimeStatusRes.json().catch(() => ({}))
            setRuntimeStatus(json?.data || null)
          }
          if (runtimeMetricsRes.ok) {
            const json = await runtimeMetricsRes.json().catch(() => ({}))
            setRuntimeMetrics(json?.data || null)
          }
          if (runtimeRulepacksRes.ok) {
            const json = await runtimeRulepacksRes.json().catch(() => ({}))
            setRuntimeRulepacks(json?.data || null)
          }
          if (runtimeHealthRes.ok) {
            const json = await runtimeHealthRes.json().catch(() => ({}))
            setRuntimeHealth(json?.data || null)
          }
        }
      } catch {}
    }
    void loadAiFoundation()
    return () => {
      active = false
    }
  }, [token, loadLegacyAiStatus, loadFoundationRuntime])

  const saveAi = async () => {
    const res = await fetch(`${API_ENDPOINTS.AI_ASSISTANT}/${aiProvider}/config`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(aiConfig),
    })
    if (featureFlags.AI_AUDIT_ASSISTANT_V1) {
      await fetch(API_ENDPOINTS.AI_PROVIDERS, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          providerKey: aiProvider,
          displayName: aiProvider,
          endpoint: aiConfig.endpoint || null,
          enabled: true,
          secureMode: secureInferenceMode,
          localMode: localModelMode,
          euOnlyMode: false,
        }),
      }).catch(() => {})
    }
    if (!res.ok) {
      const msg = await res.json().catch(() => ({}))
      alert(msg?.error || 'Failed to save AI configuration')
    }
  }

  const buildAiTestPayload = () =>
    JSON.stringify({
      message: 'Ping',
      apiKey: aiConfig.apiKey || undefined,
      endpoint: aiConfig.endpoint || undefined,
      model: aiConfig.model || undefined,
      apiVersion: aiConfig.apiVersion || undefined,
    })

  const aiFormGrid = (
    <form
      className={enterpriseUx ? entStyles.formGrid : styles.formGrid}
      onSubmit={(e) => e.preventDefault()}
      autoComplete="off"
    >
      <Select<string>
        label="Provider"
        minWidth="100%"
        variant={enterpriseUx ? 'outline' : 'default'}
        options={[...AI_PROVIDER_OPTIONS]}
        value={aiProvider || null}
        onChange={(v) => setAiProvider(v ?? 'gemini')}
      />
      <TextField
        label="API Key"
        type="password"
        value={aiConfig.apiKey || ''}
        onChange={(v) => setAiConfig({ ...aiConfig, apiKey: v })}
        minWidth="100%"
        autoComplete="off"
      />
      <TextField
        label="API Base URL"
        placeholder={aiProvider === 'gemini' ? 'https://generativelanguage.googleapis.com' : 'https://api.openai.com/v1'}
        value={aiConfig.endpoint || ''}
        onChange={(v) => setAiConfig({ ...aiConfig, endpoint: v })}
        minWidth="100%"
      />
      <TextField
        label="API Version"
        placeholder="v1beta"
        value={aiConfig.apiVersion || ''}
        onChange={(v) => setAiConfig({ ...aiConfig, apiVersion: v })}
        minWidth="100%"
      />
      <TextField
        label="Project Number (Gemini)"
        placeholder=""
        value={aiConfig.projectNumber || ''}
        onChange={(v) => setAiConfig({ ...aiConfig, projectNumber: v })}
        minWidth="100%"
      />
      <Select<string>
        label="Model"
        minWidth="100%"
        variant={enterpriseUx ? 'outline' : 'default'}
        options={[...AI_MODEL_OPTIONS]}
        value={aiConfig.model || null}
        onChange={(v) => setAiConfig({ ...aiConfig, model: v ?? '' })}
      />
      {(aiProvider === 'custom' || aiProvider === 'gemini') && (
        <>
          <div className={enterpriseUx ? entStyles.formGridFull : styles.formGridFull}>
            <TextField
              label="Headers (JSON)"
              multiline
              rows={3}
              monospace
              value={
                aiConfig.headers ||
                (aiProvider === 'gemini'
                  ? '{\n  "Content-Type": "application/json",\n  "X-goog-api-key": "${API_KEY}"\n}'
                  : '')
              }
              onChange={(v) => setAiConfig({ ...aiConfig, headers: v })}
              minWidth="100%"
            />
          </div>
          <div className={enterpriseUx ? entStyles.formGridFull : styles.formGridFull}>
            <TextField
              label="Body Template (JSON, use {{model}} and {{message}})"
              multiline
              rows={5}
              monospace
              value={
                aiConfig.bodyTemplate ||
                (aiProvider === 'gemini'
                  ? '{\n  "contents": [ { "parts": [ { "text": "{{message}}" } ] } ]\n}'
                  : '')
              }
              onChange={(v) => setAiConfig({ ...aiConfig, bodyTemplate: v })}
              minWidth="100%"
            />
          </div>
        </>
      )}
    </form>
  )

  const integrationBody = (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTitleRow}>
          <Plug className={styles.plugIcon} aria-hidden />
          <h3 className={styles.cardTitle}>AI Assistant</h3>
        </div>
      </div>
      <div className={styles.cardBodyStack}>
        <p className={styles.lead}>{t('integrations.aiLead')}</p>
        {aiFormGrid}
        <div className={styles.actionsRow}>
          <Button
            type="button"
            color="secondary"
            className={`${styles.btnSecondaryBlue} ${styles.btnPad}`}
            onClick={async () => {
              try {
                const res = await fetch(`${API_ENDPOINTS.AI_ASSISTANT}/${aiProvider}/test`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: buildAiTestPayload(),
                })
                const data = await res.json()
                alert(data.ok ? `Connected: ${data.message || 'OK'}` : `Failed: ${data.error || 'Unknown error'}`)
              } catch {
                alert('Failed to test connection')
              }
            }}
          >
            Check Connection
          </Button>
          <Button type="button" color="primary" className={styles.btnPad} onClick={() => void saveAi()}>
            Save
          </Button>
        </div>
        <div className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={secureInferenceMode}
            onChange={(e) => setSecureInferenceMode(e.target.checked)}
            disabled={!featureFlags.AI_AUDIT_ASSISTANT_V1}
          />
          <span className={styles.checkboxLabel}>Secure inference mode</span>
        </div>
        <div className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={localModelMode}
            onChange={(e) => setLocalModelMode(e.target.checked)}
            disabled={!featureFlags.AI_LOCAL_MODELS_V1}
          />
          <span className={styles.checkboxLabel}>Local model mode</span>
        </div>
        <div className={styles.metaRow}>
          <span>AI Providers: {aiProviderCount}</span>
          <span>AI Models: {aiModelCount}</span>
          <span>AI Audit Assistant: {featureFlags.AI_AUDIT_ASSISTANT_V1 ? 'Enabled' : 'Disabled'}</span>
          <span>AI Policy Generation: {featureFlags.AI_POLICY_GENERATION_V1 ? 'Enabled' : 'Disabled'}</span>
          <span>AI RAG Foundation: {featureFlags.AI_AUDIT_ASSISTANT_V1 ? 'Active' : 'Disabled'}</span>
          <span>AI Health: {aiHealthStatus}</span>
        </div>
        <div className={styles.metaRow}>
          <span>Runtime: {runtimeStatus?.runtime?.status || 'N/A'}</span>
          <span>Async runtime: {runtimeStatus?.runtime?.asyncRuntimeStatus || 'N/A'}</span>
          <span>Lifecycle: {runtimeStatus?.runtime?.lifecycleStatus || 'N/A'}</span>
          <span>Health: {runtimeHealth?.status || 'N/A'}</span>
          <span>Pending reviews: {runtimeMetrics?.metrics?.reviewQueueCount ?? 'N/A'}</span>
        </div>
        <div className={styles.metaRow}>
          <span>Cognition runtime version: {runtimeRulepacks?.runtimeVersions?.cognitionRuntimeVersion || 'N/A'}</span>
          <span>Semantics version: {runtimeRulepacks?.runtimeVersions?.semanticsVersion || 'N/A'}</span>
          <span>Replay validation: {runtimeHealth?.replayValidationAvailable ? 'Available' : 'Unavailable'}</span>
        </div>
        <div className={styles.metaRow}>
          <span>Replay total: {runtimeMetrics?.metrics?.replayValidationSummary?.total ?? 0}</span>
          <span>Replay passed: {runtimeMetrics?.metrics?.replayValidationSummary?.passed ?? 0}</span>
          <span>Replay failed: {runtimeMetrics?.metrics?.replayValidationSummary?.failed ?? 0}</span>
        </div>
        <div className={styles.metaRow}>
          <span>24h runs: {runtimeMetrics?.metrics?.windows?.last24h?.reasoningRuns ?? 0}</span>
          <span>24h failed tasks: {runtimeMetrics?.metrics?.windows?.last24h?.failedAsyncTasks ?? 0}</span>
          <span>7d runs: {runtimeMetrics?.metrics?.windows?.last7d?.reasoningRuns ?? 0}</span>
          <span>7d pending reviews: {runtimeMetrics?.metrics?.windows?.last7d?.pendingReviews ?? 0}</span>
        </div>
        <AiIntegrationsPhase3aPanel token={token} />
      </div>
    </div>
  )

  const enterpriseIntegrations = (
    <EnterpriseOperationalPage>
      <nav aria-label={t('integrations.ai.sections', 'AI integration sections')}>
        <a href="#ai-assistant">{t('integrations.aiTitle', 'AI Assistant')}</a>
        <a href="#ai-organization-providers">{t('integrations.ai.orgProviders', 'Organization AI Providers')}</a>
      </nav>
      <EnterpriseOperationalSection
        layerLabel={t('integrations.layerLabel', 'Platform integrations')}
        title={t('integrations.aiTitle', 'AI Assistant')}
        subtitle={t('integrations.aiLead')}
        testId="integrations-ai-enterprise"
      >
        <div id="ai-assistant" />
        {aiFormGrid}
        <div className={entStyles.formActions}>
          <Button
            type="button"
            size="small"
            color="outline"
            onClick={async () => {
              try {
                const res = await fetch(`${API_ENDPOINTS.AI_ASSISTANT}/${aiProvider}/test`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: buildAiTestPayload(),
                })
                const data = await res.json()
                alert(data.ok ? `Connected: ${data.message || 'OK'}` : `Failed: ${data.error || 'Unknown error'}`)
              } catch {
                alert('Failed to test connection')
              }
            }}
          >
            Check Connection
          </Button>
          <Button type="button" size="small" color="outline" onClick={() => void saveAi()}>
            Save
          </Button>
        </div>
        <label className={entStyles.checkboxRow}>
          <input
            type="checkbox"
            checked={secureInferenceMode}
            onChange={(e) => setSecureInferenceMode(e.target.checked)}
            disabled={!featureFlags.AI_AUDIT_ASSISTANT_V1}
          />
          Secure inference mode
        </label>
        <label className={entStyles.checkboxRow}>
          <input
            type="checkbox"
            checked={localModelMode}
            onChange={(e) => setLocalModelMode(e.target.checked)}
            disabled={!featureFlags.AI_LOCAL_MODELS_V1}
          />
          Local model mode
        </label>
      </EnterpriseOperationalSection>

      <section className={entStyles.layer}>
        <p className={entStyles.layerLabel}>{t('integrations.status.layerLabel', 'Integration status')}</p>
        <div className={entStyles.standardGrid}>
          <EnterpriseOperationalCard title="AI Providers">
            <p className={entStyles.postureMetricValue}>{aiProviderCount}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="AI Models">
            <p className={entStyles.postureMetricValue}>{aiModelCount}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="AI Health">
            <p className={entStyles.postureMetricValue}>{aiHealthStatus}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="Audit assistant">
            <p className={entStyles.postureMetricValue}>
              {featureFlags.AI_AUDIT_ASSISTANT_V1 ? 'Enabled' : 'Disabled'}
            </p>
          </EnterpriseOperationalCard>
        </div>
        <div className={entStyles.metaRow} style={{ marginTop: 12 }}>
          <span>Policy generation: {featureFlags.AI_POLICY_GENERATION_V1 ? 'Enabled' : 'Disabled'}</span>
          <span>RAG foundation: {featureFlags.AI_AUDIT_ASSISTANT_V1 ? 'Active' : 'Disabled'}</span>
        </div>
      </section>

      <div id="ai-organization-providers">
        <AiIntegrationsPhase3aPanel token={token} />
      </div>

      {loadFoundationRuntime ? (
      <>
      <section className={entStyles.layer}>
        <p className={entStyles.layerLabel}>Runtime Status</p>
        <div className={entStyles.standardGrid}>
          <EnterpriseOperationalCard title="Foundation">
            <p className={entStyles.postureMetricValue}>{runtimeStatus?.featureFlags?.aiFoundation ? 'Enabled' : 'Disabled'}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="Cognition Runtime">
            <p className={entStyles.postureMetricValue}>{runtimeStatus?.featureFlags?.governanceCognition ? 'Enabled' : 'Disabled'}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="Async Runtime">
            <p className={entStyles.postureMetricValue}>{runtimeStatus?.runtime?.asyncRuntimeStatus || 'N/A'}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="Provider Health">
            <p className={entStyles.postureMetricValue}>{runtimeStatus?.providerHealth?.status || 'N/A'}</p>
          </EnterpriseOperationalCard>
        </div>
      </section>

      <section className={entStyles.layer}>
        <p className={entStyles.layerLabel}>Runtime Versions</p>
        <div className={entStyles.standardGrid}>
          <EnterpriseOperationalCard title="Cognition Runtime">
            <p className={entStyles.postureMetricValue}>{runtimeRulepacks?.runtimeVersions?.cognitionRuntimeVersion || 'N/A'}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="Semantics Version">
            <p className={entStyles.postureMetricValue}>{runtimeRulepacks?.runtimeVersions?.semanticsVersion || 'N/A'}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="Rulepacks">
            <p className={entStyles.postureMetricValue}>{Object.keys(runtimeRulepacks?.rulepacks || {}).length || 0}</p>
          </EnterpriseOperationalCard>
        </div>
      </section>

      <section className={entStyles.layer}>
        <p className={entStyles.layerLabel}>Runtime Metrics</p>
        <div className={entStyles.standardGrid}>
          <EnterpriseOperationalCard title="Reasoning Runs">
            <p className={entStyles.postureMetricValue}>{runtimeMetrics?.metrics?.reasoningRunsCount ?? 0}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="Async Tasks">
            <p className={entStyles.postureMetricValue}>{runtimeMetrics?.metrics?.asyncTasksCount ?? 0}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="Pending Reviews">
            <p className={entStyles.postureMetricValue}>{runtimeMetrics?.metrics?.reviewQueueCount ?? 0}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="Lifecycle Active">
            <p className={entStyles.postureMetricValue}>{runtimeMetrics?.metrics?.lifecycleStateCounts?.ACTIVE ?? 0}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="24h Reasoning Runs">
            <p className={entStyles.postureMetricValue}>{runtimeMetrics?.metrics?.windows?.last24h?.reasoningRuns ?? 0}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="7d Reasoning Runs">
            <p className={entStyles.postureMetricValue}>{runtimeMetrics?.metrics?.windows?.last7d?.reasoningRuns ?? 0}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="7d Failed Tasks">
            <p className={entStyles.postureMetricValue}>{runtimeMetrics?.metrics?.windows?.last7d?.failedAsyncTasks ?? 0}</p>
          </EnterpriseOperationalCard>
          <EnterpriseOperationalCard title="Replay Passed / Failed">
            <p className={entStyles.postureMetricValue}>
              {(runtimeMetrics?.metrics?.replayValidationSummary?.passed ?? 0)} / {(runtimeMetrics?.metrics?.replayValidationSummary?.failed ?? 0)}
            </p>
          </EnterpriseOperationalCard>
        </div>
      </section>

      <section className={entStyles.layer}>
        <p className={entStyles.layerLabel}>Runtime Health</p>
        <div className={entStyles.metaRow}>
          <span>Status: {runtimeHealth?.status || 'N/A'}</span>
          <span>Replay validation: {runtimeHealth?.replayValidationAvailable ? 'Available' : 'Unavailable'}</span>
          <span>Last run: {runtimeHealth?.lastReasoningRun?.startedAt ? new Date(runtimeHealth.lastReasoningRun.startedAt).toLocaleString() : 'N/A'}</span>
        </div>
      </section>
      </>
      ) : null}
    </EnterpriseOperationalPage>
  )

  return enterpriseUx ? enterpriseIntegrations : integrationBody
}
